import React, { FunctionComponent, useState, useEffect } from "react";
import { ReservationState } from "../../../reducers/reservation";
import BookingSummary from "./bookingSummary";
import { Card, Club, PaymentMethod } from "../../../types";
import VoucherEntry from "./voucherEntry";
import ChampionshipSelect from "./championshipSelect";
import PaymentSelect from "./paymentSelect";
import ReservationPageContext from "./reservationPageContext";
import AddCard from "./addCard";
import Confirmation from "./confirmation";
import PartnerSelect from "./partnerSelect";
import BuddiesInvite from "./buddiesInvite";
import withRedux from "../../../withRedux";
import ReservationProps from "./reservationProps";
import {
	getReservationContext,
	addCard,
	selectCard,
	getReservationPartnerSuggestions,
	loadFriends,
	addPartnerToReservation,
	removePartnerFromReservation,
	socialFind,
	clearReservationContext,
	loggedIn,
	validateAndBook,
	getPaymentCards,
	selectPaymentNetwork,
} from "../../../actions";
import Unavailable from "./unavailable";
import PlayerWithoutAccount from "./playerWithoutAccount";
import { SocialState } from "../../../reducers/social";
import { ProfileState } from "../../../reducers/profile";
import EditConfirmation from "./editConfirmation";
import SummaryUnauthenticated from "./summaryUnauthenticated";
import PaymentNetworkSelect from "./paymentNetworkSelect";

function findDefaultCard(cards: Card[]): Card | null {
	for (let card of cards) {
		if (card.default) {
			return card;
		}
	}

	if (Array.isArray(cards) && cards.length >= 1) {
		return cards[0];
	}

	return null;
}

interface Props extends ReservationProps {
	edit: boolean;
}

const BookingWizard: FunctionComponent<Props> = ({
	edit = false,
	profile,
	reservation,
	clubInfo,
	clubDetails,
	courts,
	getReservationContext,
	addCard,
	getPaymentCards,
	validateAndBook,
	selectCard,
	selectPaymentNetwork,
	loadFriends,
	friends,
	addPartnerToReservation,
	removePartnerFromReservation,
	clearReservationContext,
	onCloseClicked,
	onLoginButtonClicked,
	onBookingSuccess,
	widgetMode,
}: Props) => {
	let [page, setPage] = useState("summary");
	const goBack = (): void => {
		setPage("summary");
	};

	if (reservation.unavailableSlot) {
		page = "unavailable";
	} else {
		if (!reservation.result && page === "confirmation") {
			page = "summary";
		}

		if (!loggedIn()) {
			page = "summaryUnauthenticated";
		}
	}

	let Page = null;
	switch (page) {
		case "summaryUnauthenticated":
			Page = SummaryUnauthenticated;
			break;

		case "summary":
			Page = BookingSummary;
			break;

		case "voucher":
			Page = VoucherEntry;
			break;

		case "partner":
			Page = PartnerSelect;
			break;

		case "championship":
			Page = ChampionshipSelect;
			break;

		case "payment":
			Page = PaymentSelect;
			break;

		case "paymentNetwork":
			Page = PaymentNetworkSelect;
			break;

		case "addCard":
			Page = AddCard;
			break;

		case "confirmation":
			Page = Confirmation;
			break;

		case "editConfirmation":
			Page = EditConfirmation;
			break;

		case "buddies":
			Page = BuddiesInvite;
			break;

		case "unavailable":
			Page = Unavailable;
			break;

		case "playerWithoutAccount":
			Page = PlayerWithoutAccount;
			break;
	}

	useEffect(() => {
		if (reservation.context) {
			if (!reservation.params.paymentMethod) {
				let defaultPaymentMethod = null;

				for (let paymentOption of reservation.context.paymentOptions) {
					if (paymentOption.available) {
						defaultPaymentMethod = paymentOption;
						if (
							paymentOption.paymentMethod === PaymentMethod.CreditCard &&
							reservation.cards.length === 0
						) {
							continue;
						} else {
							break; //use the first in the list as the default option
						}
					}
				}

				if (defaultPaymentMethod) {
					if (defaultPaymentMethod.paymentMethod === "creditcard" && reservation.cards.length > 0) {
						const defaultCard = findDefaultCard(reservation.cards);
						if (defaultCard) {
							selectCard(defaultCard.cardId);
						}
					} else {
						getReservationContext({ paymentMethod: defaultPaymentMethod.paymentMethod });
					}
				}
			}
		}
	}, [reservation.context]);

	return (
		<ReservationPageContext.Provider
			value={{
				profile,
				reservation,
				clubInfo,
				clubDetails,
				goBack,
				setPage,
				getReservationContext,
				getPaymentCards,
				addCard,
				validateAndBook,
				selectCard,
				selectPaymentNetwork,
				getReservationPartnerSuggestions,
				loadFriends,
				friends,
				addPartnerToReservation,
				removePartnerFromReservation,
				socialFind,
				clearReservationContext,
				editing: edit,
				onCloseClicked,
				courts,
				onLoginButtonClicked,
				onBookingSuccess,
				widgetMode,
			}}
		>
			<Page />
		</ReservationPageContext.Provider>
	);
};

const mapStateToProps = (
	{
		reservation,
		social: { friends },
		profile,
	}: { reservation: ReservationState; social: SocialState; profile: ProfileState },
	{ club }: { club: Club }
) => {
	return { ...club, reservation, friends, profile };
};

export default withRedux(
	{
		getReservationContext,
		addCard,
		getPaymentCards,
		validateAndBook,
		selectCard,
		selectPaymentNetwork,
		loadFriends,
		addPartnerToReservation,
		removePartnerFromReservation,
		clearReservationContext,
	},
	mapStateToProps
)(BookingWizard);
